// pages/leases.js — Leases page.
//
// Owns the `data-page="leases"` section. Renders the lease table
// with repo chip filter, state filter (held/all/released/expired),
// substring text filter, and per-row release button (when
// state.allowRevoke is true).
import { state } from "../state.js";
import { isTeamUnreachable, teamUnavailableHTML } from "../team-status.js";
import { $, loadingHTML } from "../dom.js";
import { escapeHTML, ago } from "../util.js";
import {
  parseAgentID,
  worktreeFromPurpose,
  purposeToPath,
  repoFor,
  friendlyResource,
  looksLikeHashedResource,
  resourceTypeIcon,
  toolIcon,
  shortPath,
  leaseStateIcon,
  leaseStateTip,
} from "../format.js";
import { memberName, leaseMachine } from "../members.js";
import { getWhoami } from "../whoami.js";

// renderLeasesRepoChips emits the chip bar above the leases table.
// Reads state.leaseFilterRepo to highlight the active chip.
// Returns "" when there are no repos in the current window, so the
// chip bar collapses on single-repo daemons.
function renderLeasesRepoChips(repos) {
  if (!repos || repos.length === 0) return "";
  const allActive = state.leaseFilterRepo === "" ? "active" : "";
  let html = `<div style="padding: 8px 12px; border-bottom: 1px solid var(--border); display: flex; gap: 6px; align-items: center; flex-wrap: wrap;">
    <span style="font-size: 11px; color: var(--text-muted); margin-right: 4px; text-transform: uppercase; letter-spacing: 0.05em;">Repo:</span>
    <button class="filter-chip ${allActive}" data-lease-repo="" data-tooltip="Show leases from every repo">all</button>`;
  for (const r of repos) {
    const active = state.leaseFilterRepo === r ? "active" : "";
    html += `<button class="filter-chip ${active}" data-lease-repo="${escapeHTML(r)}" data-tooltip="${escapeHTML(r)}">${escapeHTML(r)}</button>`;
  }
  html += `</div>`;
  return html;
}

// renderMineOnlyToggle emits the v0.38.0 Phase 4b "mine only"
// toggle button. Hidden entirely when whoami has no verified
// identity (anonymous / pre-OIDC sessions) — there's nothing
// to filter by, and the button would just be a confused-state
// no-op.
function renderMineOnlyToggle() {
  const who = getWhoami();
  if (!who || !who.identity || who.identity === "anonymous") return "";
  const active = state.leaseFilterMineOnly ? "active" : "";
  const tip = state.leaseFilterMineOnly
    ? "Showing only leases you triggered. Click to show all."
    : "Show only leases you triggered (filters by your verified OIDC subject).";
  return `<button class="filter-chip ${active}" data-leases-mine-only data-tooltip="${tip}" style="margin-left: auto;">
    ${state.leaseFilterMineOnly ? "✓ mine only" : "mine only"}
  </button>`;
}

export function renderLeasesPage() {
  const wrap = $("leases-table-wrap");
  if (!wrap) return;

  const source =
    state.leaseFilterState === "held"
      ? state.leases
      : state.leaseFilterState === "all"
        ? state.leasesAll
        : state.leasesAll.filter(
            (l) => l.State === state.leaseFilterState,
          );

  // Repo filter: compute the unique set of repos in the source
  // window so the chip bar always reflects what's currently
  // viewable. If the user toggled state="released" and that window
  // has only one repo, only that repo's chip shows up.
  const reposInWindow = new Set();
  for (const l of source) {
    const r = repoFor(l.Resource, l.Purpose, l.Repo);
    if (r) reposInWindow.add(r);
  }
  // Splice the mine-only toggle into the repo chip bar. The bar
  // is a flex row, so the toggle's `margin-left: auto` floats it
  // to the right end without restructuring the existing chip
  // layout. When the repo chip set is empty the chip-bar HTML
  // returns "" — wrap the toggle in its own minimal bar so it
  // still renders.
  let repoChips = renderLeasesRepoChips(
    Array.from(reposInWindow).sort(),
  );
  const mineToggle = renderMineOnlyToggle();
  if (mineToggle) {
    if (repoChips) {
      // Inject toggle before the chip bar's closing </div>.
      repoChips = repoChips.replace(/<\/div>$/, mineToggle + "</div>");
    } else {
      repoChips = `<div style="padding: 8px 12px; border-bottom: 1px solid var(--border); display: flex; gap: 6px; align-items: center; flex-wrap: wrap;">${mineToggle}</div>`;
    }
  }

  const filter = (state.leaseFilterText || "").toLowerCase();
  const filtered = source.filter((l) => {
    // Repo chip filter is exact-match; text filter is substring.
    // Both must pass.
    if (state.leaseFilterRepo) {
      const r = repoFor(l.Resource, l.Purpose, l.Repo);
      if (r !== state.leaseFilterRepo) return false;
    }
    if (!filter) return true;
    const repo = repoFor(l.Resource, l.Purpose, l.Repo);
    return [l.AgentID, l.Resource, l.Purpose, l.LeaseID, repo].some(
      (f) => (f || "").toLowerCase().includes(filter),
    );
  });

  if (source.length === 0 && isTeamUnreachable()) {
    wrap.innerHTML = teamUnavailableHTML("Leases");
    return;
  }
  if (source.length === 0 && state.loading) {
    wrap.innerHTML = loadingHTML("leases");
    return;
  }

  if (filtered.length === 0) {
    wrap.innerHTML =
      repoChips +
      `<div class="empty">
        <div class="empty-title">${filter || state.leaseFilterRepo ? "No leases match this filter" : "No leases yet"}</div>
        <div class="empty-detail">${filter || state.leaseFilterRepo ? "Try a different filter, or click &ldquo;All&rdquo; to see released and expired leases." : 'Leases appear here when team members&rsquo; AI sessions edit files. <a href="https://docs.agentcollision.com/quickstart.html" style="font-weight: 600;">Get started &rarr;</a>'}</div>
      </div>`;
    return;
  }

  wrap.innerHTML =
    repoChips +
    `<table class="lease-table">
      <thead>
        <tr>
          <th style="width: 5%; text-align: center;">State</th>
          <th style="width: 9%;">User</th>
          <th style="width: 11%;">Repo</th>
          <th style="width: 20%;">Resource</th>
          <th style="width: 11%;">Agent</th>
          <th style="width: 21%;">Purpose</th>
          <th style="width: 8%; text-align: right;">Age</th>
          <th style="width: 11%; text-align: right;">Actions</th>
        </tr>
      </thead>
      <tbody>
        ${filtered
          .map((l) => {
            const agent = parseAgentID(l.AgentID);
            const wt = worktreeFromPurpose(l.Purpose);
            const filePath = purposeToPath(l.Purpose);
            const repo = repoFor(l.Resource, l.Purpose, l.Repo);
            const releasing = state.releasingLeases.has(l.LeaseID);
            return `<tr${releasing ? ' class="lease-releasing"' : ""}>
              <td style="text-align: center;"><span data-tooltip="${leaseStateTip(l.State)}" style="font-size: 16px; cursor: default;">${leaseStateIcon(l.State)}</span></td>
              <td style="font-size: 12px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" data-tooltip="${escapeHTML(leaseMachine(l))}">${escapeHTML(memberName(leaseMachine(l)) || "--")}</td>
              <td style="font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" data-tooltip="${escapeHTML(repo || "unknown repo")}">${repo ? `<span class="type-badge" style="background: var(--bg-soft); color: var(--text-secondary); font-family: var(--font-mono, monospace); font-size: 10px;">${escapeHTML(repo)}</span>` : '<span style="color: var(--text-tertiary);">--</span>'}</td>
              <td class="resource-cell" data-tooltip="${escapeHTML(l.Resource)}">${(() => {
                const friendly = friendlyResource(l.Resource);
                const hashedNoPlain = !friendly && looksLikeHashedResource(l.Resource);
                // Mirror the events-page resource cell (format.js): only nudge
                // to import a key when one ISN'T loaded. With the key loaded a
                // still-hashed path is unrecoverable (no hint), so show a
                // neutral "no path recorded" — not a misleading "needs decrypt
                // key" pointing at a key the user already has.
                if (hashedNoPlain && !state.hmacKey) {
                  return `<span style="margin-right: 4px;">${resourceTypeIcon(l.Resource)}</span><a href="#" data-page-link="machines" data-tooltip="This lease's file path is scrambled with your team key, which isn't loaded in this browser yet. Click to import it from Machines → File Path Decryption." style="color: var(--warning); text-decoration: none; font-weight: 600;">needs decrypt key</a>`;
                }
                if (hashedNoPlain) {
                  return `<span style="margin-right: 4px;">${resourceTypeIcon(l.Resource)}</span><span data-tooltip="This lease carries no recoverable file path — it was synced without a path hint. Your decryption key is loaded; there's nothing to decrypt here." style="color: var(--text-tertiary); font-style: italic;">no path recorded</span>`;
                }
                return `<span style="margin-right: 4px;">${resourceTypeIcon(l.Resource)}</span>${escapeHTML(friendly)}`;
              })()}</td>
              <td class="agent-cell" data-tooltip="${escapeHTML(l.AgentID)}">
                <span>${toolIcon(agent.tool)}</span>
                <span style="color: var(--info); font-weight: 600;">${escapeHTML(agent.tool)}</span>
                <span style="color: var(--text-tertiary);">${escapeHTML(agent.shortID)}</span>
              </td>
              <td style="font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" data-tooltip="${escapeHTML(filePath ? shortPath(filePath) : l.Purpose || "")}">
                ${filePath ? escapeHTML(shortPath(filePath).split("/").pop()) : escapeHTML(l.Purpose || "--")}
                ${wt ? `<span style="color: var(--text-tertiary); margin-left: 2px; font-size: 9px;">${escapeHTML(wt)}</span>` : ""}
              </td>
              <td class="age-cell">${escapeHTML(ago(l.AcquiredAt))}</td>
              <td>${releasing
                ? `<span class="releasing-label"><span class="loading-spinner"></span>Releasing…</span>`
                : l.State === "held" && state.allowRevoke ? `<button class="btn btn-sm btn-danger" data-release="${escapeHTML(l.LeaseID)}">Release</button>` : ""}</td>
            </tr>`;
          })
          .join("")}
      </tbody>
    </table>`;
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  renderLeasesPage();
  tickHandler = () => {
    if (state.activePage === "leases") renderLeasesPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="leases"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}

// Re-export renderLeasesRepoChips so bootstrap.js's
// renderLeasesPage (which still calls it via the bridge) doesn't
// throw. The export name matches the bridge entry. When legacy's
// renderLeasesPage is deleted in a later step, this export can go.
export { renderLeasesRepoChips };
